import Mustache from 'mustache';
import ObjectValues from 'object.values';

import * as _ from 'lodash';

export const PAGE_COLLECTIONS = 'collections';
export const PAGE_PRODUCTS = 'products';
export const PAGE_INDEX = 'index';
export const PAGE_SEARCH = 'search';

/**
 *
 */
export default class AppLibrary {
  /**
   * @type {Snippet}
   */
  snippet;

  /**
   * @type {string}
   */
  appName;

  /**
   * @param {string} appName
   * @param {Snippet} snippet
   * @param {Customization} customization
   */
  constructor(appName, snippet, customization) {
    this.appName = appName;
    this.snippet = snippet;
    this.customization = customization;
    this.__loadStatic_cache = {};

    if (!Object.values) {
      ObjectValues.shim();
    }
  }

  /**
   * @param {string} name
   * @param {string} value
   * @param {{}} options
   */
  setCookie(name, value, options = {}) {
    name = this.appName + '-' + name;
    let expires = typeof (options.expires) !== 'undefined' ? options.expires : 0;

    if (typeof expires === 'number' && expires) {
      let d = new Date();
      d.setTime(d.getTime() + expires * 1000);
      expires = options.expires = d;
    }
    if (expires && expires.toUTCString) {
      options.expires = expires.toUTCString();
    }

    value = encodeURIComponent(value);

    let updatedCookie = name + '=' + value;

    for (let propName in options) {
      updatedCookie += '; ' + propName;
      let propValue = options[propName];
      if (propValue !== true) {
        updatedCookie += '=' + propValue;
      }
    }

    document.cookie = updatedCookie;
  };

  /**
   * @param {string} name
   * @return {string|null}
   */
  getCookie(name) {
    name = this.appName + '-' + name;
    let matches = document.cookie.match(new RegExp(
        "(?:^|; )" + name.replace(/([\.$?*|{}\(\)\[\]\\\/\+^])/g, '\\$1') + "=([^;]*)"
    ));
    return matches ? decodeURIComponent(matches[1]) : null;
  };

  /**
   * @param {string} name
   */
  deleteCookie(name) {
    this.setCookie(name, "", {
      expires: -1
    })
  };

  /**
   * @param {HTMLElement|string} target
   * @param {HTMLElement} element
   * @param {string} position "before", "after" or "inside"
   * @return {boolean}
   */
  appendElement(target, element, position = 'after') {
    if (!target) {
      return false;
    }
    let sibling = typeof target === 'string' ? document.querySelector(target) : target;

    if (sibling) {
      if (position === 'inside') {
        sibling.appendChild(element);
      } else if (position === 'before') {
        sibling.parentElement.insertBefore(element, sibling);
      } else {
        if (sibling.nextElementSibling) {
          sibling.parentElement.insertBefore(element, sibling.nextElementSibling);
        } else {
          sibling.parentElement.appendChild(element);
        }
      }
      return true;
    } else {
      throw new Error('Failed to find target element');
    }
  };

  /**
   * @return {string}
   */
  getCurrentPage() {
    let urlParts = window.location.pathname.split('/');

    if (!urlParts[1] || urlParts[1].length === 2) {
      return PAGE_INDEX;
    }

    if((urlParts[1] === PAGE_COLLECTIONS && urlParts[3] === PAGE_PRODUCTS) ||
      (urlParts[2] === PAGE_COLLECTIONS && urlParts[4] === PAGE_PRODUCTS) ||
      (urlParts[2] === PAGE_PRODUCTS)) {
      return PAGE_PRODUCTS;
    }
    return urlParts[1];
  };

  /**
   * @param {Window|Node} element
   * @param {string} event
   * @param {function} handler
   * @param {boolean} useCapture
   */
  addEventListener(element, event, handler, useCapture = false) {
    if (event.indexOf(' ') !== -1) {
      event.split(' ').forEach(
          (ev) => this.addEventListener(element, ev, handler, useCapture)
      );
      return;
    }
    if (element.addEventListener) {
      element.addEventListener(event, handler, useCapture);
    } else if (element.attachEvent) {
      element.attachEvent('on' + event, handler);
    } else {
      element['on' + event] = handler;
    }
  }

  /**
   * @param {function} handler
   */
  onLoad(handler) {
    if (
        document.readyState === 'complete' ||
        (document.readyState === 'interactive' && !/MSIE *\d+\.\w+/i.test(window.navigator.userAgent)) ||	// в IE в этом режиме работа с DOM возможна, только если все скрипты разместить в футере
        document.readyState === 'loaded'	// Вариант для некоторых старых браузеров
    ) {
      handler();
    } else {
      this.addEventListener(
          document,
          'DOMContentLoaded',
          () => handler()
      );
    }
  }

  /**
   * @return {{}}
   */
  requestGetVars() {
    return window.location.search.replace('?', '').split('&').reduce(
        (result, item) => {
          let parameter = item.split('=');
          result[decodeURIComponent(parameter[0])] = decodeURIComponent(parameter[1]);
          return result;
        },
        {}
    );
  }

  /**
   * @link https://github.com/janl/mustache.js
   * @return {{render: mustacheRender, parse: mustacheParse}}
   */
  mustache() {
    return Mustache;
  }

  /**
   * @callback mustacheRender
   * @param {string} template
   * @param {{}} view
   * @param {{}} [partials]
   * @return {string}
   */

  /**
   * @callback mustacheParse
   * @param {string} template
   * @return {string}
   */

  /**
   * @param {string|[string]} url one or more
   * @param {function()} callback
   * @param {string} type 'js' or 'css'
   */
  loadStatic(url, callback = () => null, type = '') {
    if (Array.isArray(url)) {
      if (!url.length) {
        callback();
      } else {
        let loadedCnt = 0;
        url.forEach(u => this.loadStatic(
            u,
            () => {
              loadedCnt++;
              if (loadedCnt === url.length) {
                callback();
              }
            },
            type
        ));
      }
      return;
    }

    if (typeof (this.__loadStatic_cache[url]) !== 'undefined') {
      callback();
      return;
    }

    if (type !== 'js' && type !== 'css') {
      type = url.toLowerCase().split('?')[0].split('#')[0].split('.').pop();
    }
    if (type !== 'js' && type !== 'css') {
      throw new Error('Undefined type of static file "' + url + '"');
    }
    let tag;
    if (type === 'js') {
      tag = document.createElement('script');
      tag.type = 'text/javascript';
    } else {
      tag = document.createElement('link');
      tag.type = 'text/css';
      tag.rel = 'stylesheet';
    }
    if (tag.readyState) {// If the browser is Internet Explorer.
      tag.onreadystatechange = () => {
        if (tag.readyState === 'loaded' || tag.readyState === 'complete') {
          tag.onreadystatechange = null;
          this.__loadStatic_cache[url] = 1;
          callback();
        }
      };
    } else { // For any other browser.
      tag.onload = () => {
        this.__loadStatic_cache[url] = 1;
        callback();
      };
    }
    if (type === 'js') {
      tag.src = url;
    } else {
      tag.href = url;
    }
    document.getElementsByTagName('head')[0].appendChild(tag);
  };

  /**
   * @param {string} method
   * @param {string} url
   * @param {*} data
   * @param {function(responseText: string, status: number, statusText: string)} callback
   * @param {boolean} noXHeaders
   */
  ajax(method = 'GET', url, data = null, callback = () => null, noXHeaders = false) {
    if (typeof (XMLHttpRequest) === 'undefined') {
      throw new Error('XMLHttpRequest is not supported');
    }

    let XmlHttpObject = new XMLHttpRequest();
    url += (url.indexOf('?') === -1 ? '?' : '&') + 'hash=' + Math.random();
    method = method.toUpperCase();

    // Make request
    XmlHttpObject.open(method, url, true);
    XmlHttpObject.onreadystatechange = function () {
      if (XmlHttpObject.readyState === 4) {
        callback(
            XmlHttpObject.responseText,
            XmlHttpObject.status,
            XmlHttpObject.statusText
        );
      }
    };
    if (method === 'POST') {
      XmlHttpObject.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');
    }
    if (!noXHeaders) {
      XmlHttpObject.setRequestHeader('X-Requested-With', 'xmlhttprequest'); // HTTP_X_REQUESTED_WITH
    }

    if (data !== null && typeof (data) !== 'string') {
      data = JSON.stringify(data);
    }
    XmlHttpObject.send(data);
  };

  /**
   * @param {function({country_code:string,country_name:string,city:string,postal:string,latitude:number,longitude:number,IPv4:string,state:string})} callback
   */
  getGeoLocation(callback) {
    this.ajax(
        'GET',
        'https://geolocation-db.com/json/',
        null,
        data => callback(JSON.parse(data)),
        true
    );
  }

  /**
   * @param {[]} array
   * @param {function} callback
   * @returns {Promise<void>}
   */
  async asyncForEach(array, callback) {
    for (let index = 0; index < array.length; index++) {
      await callback(array[index], index, array);
    }
  }

  /**
   * Filter properties with `names-numbers`
   * @param {{}} styleData
   * @return {{}}
   */
  static processParsedStyle(styleData) {
    const UNUSED_STYLE_PROPERTIES = [
      'width', 'height', 'inline-size', 'block-size', 'min-block-size', 'min-height',
      'margin', 'margin-top', 'margin-bottom', 'margin-left', 'margin-right', 'shape-margin', 'margin-block-start',
      'opacity', 'visibility'
    ];
    const NEED_ROUND_PROPERTIES = [
      'border', 'border-width',
      'border-top', 'border-right', 'border-bottom', 'border-left',
      'border-top-width', 'border-right-width', 'border-bottom-width', 'border-left-width',
      'border-block-end', 'border-block-end-width', 'border-block-start', 'border-block-start-width',
      'border-inline-end', 'border-inline-end-width', 'border-inline-start', 'border-inline-start-width',
      'webkit-border-after', 'webkit-border-after-width', 'webkit-border-before', 'webkit-border-before-width',
      'webkit-border-end', 'webkit-border-end-width', 'webkit-border-start', 'webkit-border-start-width'
    ];
    const roundPixels = value => {
      let regexp = /^(\d*\.\d+)(px.*$)/;
      if (!regexp.test(value)) {
        return value;
      }
      let matches = regexp.exec(value);
      let number = parseFloat(matches[1]);
      return Math.round(number) + matches[2];
    };
    const getStyleKeys = styles => {
      let keys = [];
      for(let i = 0; i < styles.length; i++){
        keys.push(styles[i]);
      }
      return keys;
    };

    let parsedAtcStyle = {};
    getStyleKeys(styleData)
        .filter(cssProperty => UNUSED_STYLE_PROPERTIES.indexOf(cssProperty) === -1)
        .forEach(cssProperty => parsedAtcStyle[cssProperty] = styleData[_.camelCase(cssProperty)]);
    NEED_ROUND_PROPERTIES.forEach(prop => {
      if (typeof(parsedAtcStyle[prop]) !== 'undefined') {
        parsedAtcStyle[prop] = roundPixels(parsedAtcStyle[prop]);
      }
    });
    return parsedAtcStyle;
  }

  /**
   * @type {number}
   * @private
   */
  static _warningsLimit = 100;

  /**
   * @param {*} message
   * @param {string} level
   */
  static consoleLog(message, level = 'log') {
    if (typeof (console[level]) !== 'function') {
      throw `Method window.console.${level}() is not exists`;
    }
    if (this._warningsLimit > 0) {
      console[level](message);
      this._warningsLimit--;
      return;
    }
    if (this._warningsLimit === 0) {
      console.error('Too many warnings!');
      this._warningsLimit--;
    }
  }
}
